<template>
  <div class="p-6 dark:text-white">
    <!-- Header -->
    <div class="mb-6">
      <h3 class="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
        Auditoria de Assinaturas Pix Automático
      </h3>
      <p class="text-gray-600 dark:text-gray-400">
        Análise detalhada de assinaturas Pix Automático para identificação de inconsistências
      </p>
    </div>

    <!-- Formulário de Busca -->
    <div class="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <form @submit.prevent="search" class="space-y-3">
        <div class="flex gap-3">
          <div class="flex-1">
            <label for="producer-email" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email do Produtor
            </label>
            <input
              id="producer-email"
              v-model="producerEmail"
              type="email"
              placeholder="exemplo@email.com"
              class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
              :disabled="loading"
            />
          </div>
          <div class="flex items-end">
            <button
              type="submit"
              :disabled="loading"
              class="px-6 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <span v-if="loading" class="flex items-center gap-2">
                <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Buscando...
              </span>
              <span v-else>Buscar Assinaturas</span>
            </button>
          </div>
        </div>

        <!-- Filtro de Inconsistências -->
        <div class="flex items-center gap-2">
          <input
            id="only-inconsistencies"
            v-model="onlyInconsistencies"
            type="checkbox"
            class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
            :disabled="loading"
          />
          <label for="only-inconsistencies" class="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <span class="font-medium">🔍 Apenas assinaturas com inconsistências</span>
            <span class="ml-2 text-gray-500 dark:text-gray-400">(transações pagas que foram canceladas)</span>
          </label>
        </div>
      </form>
    </div>

    <!-- Resultados -->
    <div v-if="searchResults" class="mb-6">
      <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div class="flex justify-between items-center">
          <div>
            <strong class="text-blue-900 dark:text-blue-100">Produtor:</strong>
            <span class="ml-2 text-blue-800 dark:text-blue-200">
              {{ searchResults.producer.name }} ({{ searchResults.producer.email }})
            </span>
          </div>
          <div>
            <strong class="text-blue-900 dark:text-blue-100">Total de Assinaturas:</strong>
            <span class="ml-2 text-blue-800 dark:text-blue-200 font-semibold">
              {{ searchResults.total }}
            </span>
          </div>
          <button
            v-if="searchResults.total > 0"
            @click="exportData"
            :disabled="exporting"
            class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm"
          >
            {{ exporting ? 'Exportando...' : 'Exportar Excel' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Tabela usando TableComponent -->
    <table-component
      v-if="searchResults"
      :headers="tableHeaders"
      :loading="loading"
      :is-empty="subscriptions.length === 0"
    >
      <tr
        v-for="sub in subscriptions"
        :key="sub.subscription_id"
        :class="{
          'bg-red-50 dark:bg-red-900/20': sub.has_inconsistencies,
          'hover:bg-gray-50 dark:hover:bg-gray-700': true,
          'cursor-pointer': true
        }"
        @click="openDetails(sub.subscription_id)"
      >
        <td class="px-4 py-3 text-sm text-gray-900 dark:text-gray-300">{{ sub.subscription_id }}</td>
        <td class="px-4 py-3 text-sm">
          <div class="font-medium text-gray-900 dark:text-white">{{ sub.buyer_name }}</div>
          <div class="text-gray-500 dark:text-gray-400 text-xs">{{ sub.buyer_email }}</div>
        </td>
        <td class="px-4 py-3 text-sm">
          <div class="font-medium text-gray-900 dark:text-white">{{ sub.product_name }}</div>
          <div class="text-gray-500 dark:text-gray-400 text-xs">{{ sub.offer_name }}</div>
        </td>
        <td class="px-4 py-3">
          <span :class="getPixStatusBadge(sub.pix_status)">
            {{ sub.pix_status }}
          </span>
        </td>
        <td class="px-4 py-3 text-sm">
          <div class="flex items-center gap-2">
            <span class="text-green-600 dark:text-green-400">✅ {{ sub.successful_charges }}</span>
            <span v-if="sub.failed_charges > 0" class="text-red-600 dark:text-red-400">
              ❌ {{ sub.failed_charges }}
            </span>
          </div>
        </td>
        <td class="px-4 py-3 text-sm text-gray-900 dark:text-gray-300">
          {{ formatDate(sub.next_charge) }}
        </td>
        <td class="px-4 py-3">
          <span class="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded text-xs font-medium">
            {{ sub.transactions_count }}
          </span>
        </td>
        <td class="px-4 py-3 text-center">
          <span
            v-if="sub.has_inconsistencies"
            class="px-2 py-1 bg-red-600 text-white rounded text-xs font-bold"
            :title="getInconsistenciesText(sub.inconsistencies)"
          >
            {{ sub.inconsistencies.length }} ⚠️
          </span>
          <span v-else class="text-gray-400 dark:text-gray-600">—</span>
        </td>
      </tr>
    </table-component>

    <!-- Empty State -->
    <empty-state
      v-if="searchResults && subscriptions.length === 0"
      message="Nenhuma assinatura Pix Automático encontrada para este produtor"
    />

    <!-- Modal de Detalhes -->
    <modal :open="detailsModalOpen" size="7xl" @close="closeDetails">
      <div class="p-6">
        <div class="flex justify-between items-center mb-6">
          <h4 class="text-xl font-semibold text-white-900 dark:text-white">
            Detalhes da Assinatura #{{ selectedSubscriptionId }}
          </h4>
          <button
            @click="closeDetails"
            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Loading -->
        <ticto-loading v-if="loadingDetails" />

        <!-- Detalhes -->
        <div v-else-if="details">
          <!-- Alertas de Inconsistências -->
          <div
            v-if="details.inconsistencies && details.inconsistencies.length > 0"
            class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
          >
            <h5 class="font-semibold text-red-900 dark:text-red-100 mb-3 flex items-center gap-2">
              <span class="text-2xl">⚠️</span>
              Inconsistências Detectadas
            </h5>
            <ul class="space-y-2">
              <li
                v-for="(issue, idx) in details.inconsistencies"
                :key="idx"
                class="text-sm"
              >
                <span
                  :class="{
                    'font-bold text-red-700 dark:text-red-300': issue.severity === 'critical',
                    'font-semibold text-orange-700 dark:text-orange-300': issue.severity === 'medium'
                  }"
                >
                  [{{ issue.severity.toUpperCase() }}]
                </span>
                <span class="ml-2 text-red-800 dark:text-red-200">{{ issue.message }}</span>
                <span v-if="issue.transaction_id" class="ml-2 text-red-600 dark:text-red-400 text-xs">
                  (Transaction ID: {{ issue.transaction_id }})
                </span>
              </li>
            </ul>
          </div>

          <!-- Tabs -->
          <div class="border-b border-gray-200 dark:border-gray-700 mb-6">
            <nav class="-mb-px flex space-x-8">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                @click="activeTab = tab.id"
                :class="[
                  'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                ]"
              >
                {{ tab.label }}
                <span v-if="tab.count" class="ml-2 px-2 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">
                  {{ tab.count }}
                </span>
              </button>
            </nav>
          </div>

          <!-- Tab Content -->
          <div class="overflow-auto max-h-[600px]">
            <!-- Subscription Tab -->
            <div v-show="activeTab === 'subscription'">
              <data-table :data="details.subscription" />
            </div>

            <!-- PIX Recurring Tab -->
            <div v-show="activeTab === 'pix'">
              <data-table v-if="details.pix_recurring" :data="details.pix_recurring" />
              <p v-else class="text-gray-500 dark:text-gray-400">Sem dados Pix Automático</p>
            </div>

            <!-- Order Tab -->
            <div v-show="activeTab === 'order'">
              <data-table v-if="details.order" :data="details.order" />
              <p v-else class="text-gray-500 dark:text-gray-400">Sem dados de Order</p>
            </div>

            <!-- Transactions Tab -->
            <div v-show="activeTab === 'transactions'" class="space-y-4">
              <div
                v-for="transaction in details.transactions"
                :key="transaction.id"
                :class="[
                  'border rounded-lg p-4',
                  transaction.is_inconsistent
                    ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                    : 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800'
                ]"
              >
                <!-- Header da Transaction -->
                <div class="flex justify-between items-center mb-4">
                  <span class="font-semibold text-gray-900 dark:text-white">Transaction #{{ transaction.id }}</span>
                  <div class="flex items-center gap-2">
                    <span
                      v-if="transaction.is_inconsistent"
                      class="px-2 py-1 bg-red-600 text-white rounded text-xs font-bold"
                    >
                      ⚠️ CRÍTICO: Authorized → Cancelled
                    </span>
                    <span class="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded text-xs font-medium">
                      {{ transaction.status_flow }}
                    </span>
                  </div>
                </div>

                <!-- Alerta de Inconsistência -->
                <div v-if="transaction.is_inconsistent" class="bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg p-3 mb-4">
                  <h6 class="font-semibold text-red-900 dark:text-red-100 mb-2">
                    🚨 Transação Paga foi Cancelada
                  </h6>
                  <div class="text-sm text-red-800 dark:text-red-200 space-y-1">
                    <p><strong>Valor:</strong> {{ transaction.amount_formatted || transaction.amount }}</p>
                    <p><strong>Pago em:</strong> {{ getStatusDate(transaction.status_history, 'authorized') }}</p>
                    <p><strong>Cancelado em:</strong> {{ getStatusDate(transaction.status_history, 'canceled') }}</p>
                  </div>
                  <p class="text-xs text-red-700 dark:text-red-300 mt-2 border-t border-red-300 dark:border-red-700 pt-2">
                    Esta transação foi marcada como paga (authorized) e posteriormente cancelada.
                    Isso pode indicar um problema no processamento de webhooks de cancelamento.
                  </p>
                </div>

                <!-- Dados da Transaction -->
                <data-table :data="transaction" :exclude="['status_history']" />

                <!-- Histórico de Status -->
                <div v-if="transaction.status_history && transaction.status_history.length > 0" class="mt-4">
                  <h6 class="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    📜 Histórico de Mudanças de Status
                  </h6>
                  <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                      <thead class="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th class="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Data/Hora</th>
                          <th class="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Status</th>
                          <th class="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Observação</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                        <tr
                          v-for="(history, idx) in transaction.status_history"
                          :key="idx"
                          :class="{
                            'bg-green-50 dark:bg-green-900/20': history.status === 'authorized',
                            'bg-red-50 dark:bg-red-900/20': history.status === 'canceled' && hasPreviousAuthorized(transaction.status_history, idx)
                          }"
                        >
                          <td class="px-4 py-2 text-gray-900 dark:text-gray-300">{{ history.created_at }}</td>
                          <td class="px-4 py-2">
                            <span :class="getStatusBadge(history.status)">
                              {{ history.status }}
                            </span>
                          </td>
                          <td class="px-4 py-2 text-xs text-gray-700 dark:text-gray-400">
                            <span v-if="history.status === 'canceled' && hasPreviousAuthorized(transaction.status_history, idx)" class="text-red-700 dark:text-red-300 font-semibold">
                              ⚠️ CRÍTICO: Transação foi cancelada após estar paga (authorized)
                            </span>
                            <span v-else-if="history.status === 'authorized'" class="text-green-700 dark:text-green-300 font-semibold">
                              ✅ Pagamento confirmado
                            </span>
                            <span v-else class="text-gray-600 dark:text-gray-400">
                              Mudança de status registrada
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- Subscription Histories Tab -->
            <div v-show="activeTab === 'histories'" class="space-y-3">
              <div
                v-for="history in details.subscription_histories"
                :key="history.id"
                class="border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg p-3"
              >
                <div class="flex justify-between items-center mb-3">
                  <span class="font-semibold text-sm text-gray-900 dark:text-white">
                    Status: <span :class="getStatusBadge(history.status)">{{ history.status }}</span>
                  </span>
                  <span class="text-xs text-gray-600 dark:text-gray-400">
                    Registrado em: {{ history.created_at }}
                  </span>
                </div>
                <div class="text-sm text-gray-800 dark:text-gray-300">
                  <strong class="text-gray-900 dark:text-white">Data de Referência:</strong> {{ history.reference_date }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import axios from 'axios';
import TableComponent from '../../components/layouts/table/TableComponent.vue';
import Modal from '../../components/layouts/Modal.vue';
import EmptyState from '../../components/layouts/EmptyState.vue';
import TictoLoading from '../../components/layouts/TictoLoading.vue';
import DataTable from './components/DataTable.vue';

export default {
  components: {
    TableComponent,
    Modal,
    EmptyState,
    TictoLoading,
    DataTable
  },
  data() {
    return {
      producerEmail: '',
      onlyInconsistencies: true, // Padrão: mostrar apenas problemáticas
      loading: false,
      exporting: false,
      searchResults: null,
      detailsModalOpen: false,
      selectedSubscriptionId: null,
      loadingDetails: false,
      details: null,
      activeTab: 'subscription',
      tableHeaders: [
        'ID',
        'Comprador',
        'Produto/Oferta',
        'PIX Status',
        'Cobranças',
        'Próxima Cobrança',
        'Transações',
        '⚠️'
      ]
    };
  },
  computed: {
    subscriptions() {
      return this.searchResults?.data || [];
    },
    tabs() {
      if (!this.details) return [];

      return [
        { id: 'subscription', label: 'Subscription' },
        { id: 'pix', label: 'Pix Automático' },
        { id: 'order', label: 'Order' },
        { id: 'transactions', label: 'Transactions', count: this.details.transactions?.length || 0 },
        { id: 'histories', label: 'Subscription History', count: this.details.subscription_histories?.length || 0 }
      ];
    }
  },
  methods: {
    async search() {
      this.loading = true;
      this.searchResults = null;

      try {
        const { data } = await axios.get('/api/pix-recurring/audit/search', {
          params: {
            producer_email: this.producerEmail,
            only_inconsistencies: Boolean(this.onlyInconsistencies)
          }
        });

        if (data.success) {
          this.searchResults = data;
        } else {
          this.$notify({
            group: "error",
            title: "Erro",
            text: data.message || 'Erro ao buscar assinaturas'
          }, 4000);
        }
      } catch (error) {
        console.error('Erro na busca:', error);
        this.$notify({
          group: "error",
          title: "Erro",
          text: error.response?.data?.message || 'Erro ao buscar assinaturas'
        }, 4000);
      } finally {
        this.loading = false;
      }
    },

    async openDetails(subscriptionId) {
      this.selectedSubscriptionId = subscriptionId;
      this.detailsModalOpen = true;
      this.loadingDetails = true;
      this.activeTab = 'subscription';

      try {
        const { data } = await axios.get(`/api/pix-recurring/audit/details/${subscriptionId}`);
        this.details = data;
      } catch (error) {
        console.error('Erro ao carregar detalhes:', error);
        this.$notify({
          group: "error",
          title: "Erro",
          text: 'Erro ao carregar detalhes da assinatura'
        }, 4000);
        this.closeDetails();
      } finally {
        this.loadingDetails = false;
      }
    },

    closeDetails() {
      this.detailsModalOpen = false;
      this.selectedSubscriptionId = null;
      this.details = null;
    },

    async exportData() {
      this.exporting = true;

      try {
        const { data } = await axios.get('/api/pix-recurring/audit/export', {
          params: {
            producer_email: this.producerEmail,
            only_inconsistencies: Boolean(this.onlyInconsistencies)
          }
        });

        if (data.success) {
          window.open(data.url, '_blank');
          this.$notify({
            group: "success",
            title: "Sucesso",
            text: 'Export gerado com sucesso'
          }, 4000);
        } else {
          this.$notify({
            group: "error",
            title: "Erro",
            text: data.message || 'Erro ao exportar'
          }, 4000);
        }
      } catch (error) {
        console.error('Erro ao exportar:', error);
        this.$notify({
          group: "error",
          title: "Erro",
          text: 'Erro ao exportar dados'
        }, 4000);
      } finally {
        this.exporting = false;
      }
    },

    getStatusBadge(status) {
      const badges = {
        active: 'px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded text-xs font-medium',
        canceled: 'px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded text-xs font-medium',
        paused: 'px-2 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded text-xs font-medium',
        expired: 'px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 rounded text-xs font-medium',
        pending: 'px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-xs font-medium',
        authorized: 'px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded text-xs font-medium',
        failed: 'px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded text-xs font-medium',
        processing: 'px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded text-xs font-medium'
      };
      return badges[status] || 'px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 rounded text-xs font-medium';
    },

    getPixStatusBadge(status) {
      const badges = {
        authorized: 'px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded text-xs font-medium',
        active: 'px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded text-xs font-medium',
        pending: 'px-2 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded text-xs font-medium',
        canceled: 'px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded text-xs font-medium',
        expired: 'px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 rounded text-xs font-medium'
      };
      return badges[status] || 'px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 rounded text-xs font-medium';
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return date;
    },

    getInconsistenciesText(inconsistencies) {
      return inconsistencies.map(i => i.message).join('\n');
    },

    hasPreviousAuthorized(statusHistory, currentIndex) {
      for (let i = 0; i < currentIndex; i++) {
        if (statusHistory[i].status === 'authorized') {
          return true;
        }
      }
      return false;
    },

    getStatusDate(statusHistory, targetStatus) {
      if (!statusHistory || !Array.isArray(statusHistory)) {
        return 'N/A';
      }

      // Busca a última ocorrência do status
      const history = [...statusHistory].reverse().find(h => h.status === targetStatus);

      return history?.created_at || 'N/A';
    }
  }
};
</script>
